import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import ProjectFormActions from "../../components/forms/ProjectForm/ProjectFormActions";

// Form data interface based on the provided image
interface ScientificCVFormData {
  // Row 1
  fullName: string;
  academicRank: string;
  birthYear: string;
  academicDegree: string;
  birthYear2: string;

  // Row 2
  department: string;
  phone: string;
  alternatePhone: string;
  address: string;

  // Row 3
  birthDay: string;
  birthMonth: string;
  birthYear3: string;
  gender: string;
  ethnicity: string;

  // Row 4
  researchField: string;
  majorCode: string;
  researchTitle: string;
  managementArea: string;

  // Row 5
  trainingPlace: string;
  trainingInstituteName: string;
  professionalRank: string;
  managementScope: string;

  // Row 6 - Ranks and Positions
  rank: string;
  rankMonth: string;
  rankYear: string;
  position: string;
  positionMonth: string;
  positionYear: string;

  // Row 7 - Personal Info
  email: string;
  idCardNumber: string;
  idIssueDay: string;
  idIssueMonth: string;
  idIssueYear: string;
  idIssuePlace: string;

  // Row 7
  birthPlace: string;

  // Row 8
  bankAccount: string;
  bankName: string;
  branch: string;

  // File attachments
  attachedFiles: File[];
}

const AddNewScientificCV: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ScientificCVFormData>({
    fullName: "",
    academicRank: "",
    birthYear: "",
    academicDegree: "",
    birthYear2: "",
    department: "",
    phone: "",
    alternatePhone: "",
    address: "",
    birthDay: "",
    birthMonth: "",
    birthYear3: "",
    gender: "",
    ethnicity: "",
    researchField: "",
    majorCode: "",
    researchTitle: "",
    managementArea: "",
    trainingPlace: "",
    trainingInstituteName: "",
    professionalRank: "",
    managementScope: "",
    rank: "",
    rankMonth: "",
    rankYear: "",
    position: "",
    positionMonth: "",
    positionYear: "",
    email: "",
    idCardNumber: "",
    idIssueDay: "",
    idIssueMonth: "",
    idIssueYear: "",
    idIssuePlace: "",
    birthPlace: "",
    bankAccount: "",
    bankName: "",
    branch: "",
    attachedFiles: [],
  });

  // Generate arrays for dropdowns
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const years = Array.from({ length: 101 }, (_, i) => 2024 - i);

  const handleInputChange = (
    field: keyof ScientificCVFormData,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileSelect = (files: FileList | null) => {
    if (files) {
      const newFiles = Array.from(files);
      setFormData((prev) => ({
        ...prev,
        attachedFiles: [...prev.attachedFiles, ...newFiles],
      }));
    }
  };

  const handleCancel = () => {
    navigate("/scientific-cv");
  };

  const handleSave = () => {
    // Handle save logic here
    console.log("Saving Scientific CV data:", formData);
    navigate("/scientific-cv");
  };

  return (
    <PageShell title="Thêm mới Lý lịch khoa học">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Thêm mới Lý lịch khoa học
          </h1>
        </div>

        {/* Form Container */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              {/* Row 1: Họ tên (2 cột) - Học hàm & Học vị (1 cột) - Năm cấp (1 cột) */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Họ tên <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập họ tên"
                  />
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Học hàm
                      </label>
                      <select
                        value={formData.academicRank}
                        onChange={(e) =>
                          handleInputChange("academicRank", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Học hàm -</option>
                        <option>Giáo sư</option>
                        <option>Phó giáo sư</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Học vị <span className="text-red-500">(*)</span>
                      </label>
                      <select
                        value={formData.academicDegree}
                        onChange={(e) =>
                          handleInputChange("academicDegree", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Học vị -</option>
                        <option>Tiến sĩ</option>
                        <option>Thạc sĩ</option>
                        <option>Cử nhân</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Năm cấp
                      </label>
                      <select
                        value={formData.birthYear}
                        onChange={(e) =>
                          handleInputChange("birthYear", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Năm cấp -</option>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Năm cấp
                      </label>
                      <select
                        value={formData.birthYear2}
                        onChange={(e) =>
                          handleInputChange("birthYear2", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Năm cấp -</option>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Đơn vị/ Phòng ban - Điện thoại - Điện thoại khác - Địa chỉ */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Đơn vị/ Phòng ban <span className="text-red-500">(*)</span>
                  </label>
                  <select
                    value={formData.department}
                    onChange={(e) =>
                      handleInputChange("department", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Đơn vị -</option>
                    <option>Khoa CNTT</option>
                    <option>Phòng Khoa học</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Điện thoại <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Điện thoại"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Điện thoại khác
                  </label>
                  <input
                    type="text"
                    value={formData.alternatePhone}
                    onChange={(e) =>
                      handleInputChange("alternatePhone", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Điện thoại khác"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Địa chỉ
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập Địa chỉ"
                  />
                </div>
              </div>

              {/* Row 3: Ngày & Tháng (1 cột) - Năm sinh - Giới tính - Dân tộc */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ngày
                      </label>
                      <select
                        value={formData.birthDay}
                        onChange={(e) =>
                          handleInputChange("birthDay", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Ngày -</option>
                        {days.map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tháng
                      </label>
                      <select
                        value={formData.birthMonth}
                        onChange={(e) =>
                          handleInputChange("birthMonth", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Tháng -</option>
                        {months.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Năm sinh <span className="text-red-500">(*)</span>
                  </label>
                  <select
                    value={formData.birthYear3}
                    onChange={(e) =>
                      handleInputChange("birthYear3", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Năm sinh -</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Giới tính
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) =>
                      handleInputChange("gender", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Giới tính -</option>
                    <option>Nam</option>
                    <option>Nữ</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dân tộc
                  </label>
                  <select
                    value={formData.ethnicity}
                    onChange={(e) =>
                      handleInputChange("ethnicity", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Dân tộc -</option>
                    <option>Kinh</option>
                    <option>Tày</option>
                    <option>Thái</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-4 rounded-lg">
              {/* Row 4: Lĩnh vực nghiên cứu - Mã chuyên ngành - Tên gọi - Điểm quản lý */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 ">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lĩnh vực nghiên cứu
                  </label>
                  <select
                    value={formData.researchField}
                    onChange={(e) =>
                      handleInputChange("researchField", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Lĩnh vực nghiên cứu -</option>
                    <option>Công nghệ thông tin</option>
                    <option>Khoa học tự nhiên</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mã chuyên ngành
                  </label>
                  <input
                    type="text"
                    value={formData.majorCode}
                    onChange={(e) =>
                      handleInputChange("majorCode", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập mã chuyên ngành nghiên cứu"
                  />
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên gọi
                  </label>
                  <input
                    type="text"
                    value={formData.researchTitle}
                    onChange={(e) =>
                      handleInputChange("researchTitle", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên gọi"
                  />
                </div>
              </div>

              {/* Row 5: Nơi đào tạo - Tên cơ sở đào tạo - Chức danh nghiên cứu - Diện quản lý */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nơi đào tạo <span className="text-red-500">(*)</span>
                  </label>
                  <select
                    value={formData.trainingPlace}
                    onChange={(e) =>
                      handleInputChange("trainingPlace", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Nơi đào tạo -</option>
                    <option>Trong nước</option>
                    <option>Nước ngoài</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tên cơ sở đào tạo
                  </label>
                  <input
                    type="text"
                    value={formData.trainingInstituteName}
                    onChange={(e) =>
                      handleInputChange("trainingInstituteName", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập nơi đào tạo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chức danh nghiên cứu{" "}
                    <span className="text-red-500">(*)</span>
                  </label>
                  <select
                    value={formData.professionalRank}
                    onChange={(e) =>
                      handleInputChange("professionalRank", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chức danh nghiên cứu -</option>
                    <option>Nghiên cứu viên chính</option>
                    <option>Nghiên cứu viên</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Diện quản lý
                  </label>
                  <select
                    value={formData.managementScope}
                    onChange={(e) =>
                      handleInputChange("managementScope", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Diện quản lý -</option>
                    <option>QNCN</option>
                    <option>SQ</option>
                    <option>Trung ương</option>
                    <option>Địa phương</option>
                  </select>
                </div>
              </div>

              {/* Row 6: Cấp bậc & Tháng (1 cột) - Năm cấp - Chức vụ & Tháng (1 cột) - Năm QĐ */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-4">
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cấp bậc
                      </label>
                      <select
                        value={formData.rank}
                        onChange={(e) =>
                          handleInputChange("rank", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Cấp bậc -</option>
                        <option>Thượng tá</option>
                        <option>Trung tá</option>
                        <option>Thiếu tá</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tháng
                      </label>
                      <select
                        value={formData.rankMonth}
                        onChange={(e) =>
                          handleInputChange("rankMonth", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Tháng -</option>
                        {months.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Năm cấp
                  </label>
                  <select
                    value={formData.rankYear}
                    onChange={(e) =>
                      handleInputChange("rankYear", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Năm -</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chức vụ
                      </label>
                      <select
                        value={formData.position}
                        onChange={(e) =>
                          handleInputChange("position", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Chức vụ -</option>
                        <option>Trưởng phòng</option>
                        <option>Phó trưởng phòng</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tháng
                      </label>
                      <select
                        value={formData.positionMonth}
                        onChange={(e) =>
                          handleInputChange("positionMonth", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Tháng -</option>
                        {months.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Năm QĐ
                  </label>
                  <select
                    value={formData.positionYear}
                    onChange={(e) =>
                      handleInputChange("positionYear", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Năm -</option>
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              {/* Row 7: Email - Số CMT/CCCD - Ngày & Tháng & Năm cấp (1 cột) - Nơi cấp */}
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Số CMT/ CCCD
                  </label>
                  <input
                    type="text"
                    value={formData.idCardNumber}
                    onChange={(e) =>
                      handleInputChange("idCardNumber", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập Số CMT/ CCCD"
                  />
                </div>
                <div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ngày
                      </label>
                      <select
                        value={formData.idIssueDay}
                        onChange={(e) =>
                          handleInputChange("idIssueDay", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Ngày -</option>
                        {days.map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tháng
                      </label>
                      <select
                        value={formData.idIssueMonth}
                        onChange={(e) =>
                          handleInputChange("idIssueMonth", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Tháng -</option>
                        {months.map((month) => (
                          <option key={month} value={month}>
                            {month}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Năm cấp
                      </label>
                      <select
                        value={formData.idIssueYear}
                        onChange={(e) =>
                          handleInputChange("idIssueYear", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option>- Năm -</option>
                        {years.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nơi cấp
                  </label>
                  <input
                    type="text"
                    value={formData.idIssuePlace}
                    onChange={(e) =>
                      handleInputChange("idIssuePlace", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nơi cấp"
                  />
                </div>
              </div>

              {/* Row 8: Tài khoản ngân hàng - Ngân hàng - Chi nhánh */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tài khoản ngân hàng
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount}
                    onChange={(e) =>
                      handleInputChange("bankAccount", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập số tài khoản"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ngân hàng
                  </label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) =>
                      handleInputChange("bankName", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập tên ngân hàng"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chi nhánh
                  </label>
                  <input
                    type="text"
                    value={formData.branch}
                    onChange={(e) =>
                      handleInputChange("branch", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập chi nhánh"
                  />
                </div>
              </div>
            </div>
            {/* File Upload */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File đính kèm (Bạn có thể chọn nhiều file một lần!)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                    id="cv-file-upload"
                  />
                  <label
                    htmlFor="cv-file-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    Choose Files
                  </label>
                  <span className="text-sm text-gray-500">
                    {formData.attachedFiles.length === 0
                      ? "No file chosen"
                      : `${formData.attachedFiles.length} file(s) chosen`}
                  </span>
                </div>

                {formData.attachedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Files:
                    </h4>
                    <div
                      className={`space-y-2 ${
                        formData.attachedFiles.length > 3
                          ? "max-h-48 overflow-y-auto pr-2"
                          : ""
                      }`}
                    >
                      {formData.attachedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <svg
                              className="w-4 h-4 text-gray-400 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const updatedFiles =
                                formData.attachedFiles.filter(
                                  (_, i) => i !== index
                                );
                              setFormData((prev) => ({
                                ...prev,
                                attachedFiles: updatedFiles,
                              }));
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                            title="Xóa file"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ProjectFormActions onSave={handleSave} onCancel={handleCancel} />
      </div>
    </PageShell>
  );
};

export default AddNewScientificCV;
